import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Optional,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FormFieldComponent } from '../../../../../../shared/components/form-field/form-field.component';
import { AzButtonComponent } from '../../../../../../shared/components/az-button/az-button.component';
import { AzDatePickerComponent } from '../../../../../../shared/components/az-date-picker/az-date-picker.component';
import { AzSelectorComponent } from '../../../../../../shared/components/az-selector/az-selector.component';
import { StatusChangeModalComponent } from '../../../../../../shared/components/status-change-modal/status-change-modal.component';
import { AzDocumentUpdateModalComponent } from '../../../../../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { UserPermissionsService } from '../../../../../../core/auth/services/user-permissions.service';
import { LoggerService } from '../../../../../../core/logging/logger.service';
import { LegalEntityService } from '../../../../services/legal-entity.service';
import { SelectedLegalEntityService } from '../../../../../../shared/services/selected-legal-entity.service';
import { LegalEntityDetailComponent } from '../../../legal-entity-detail/legal-entity-detail.component';
import { RemediationActionDto } from '../../../../../../shared/models/remediation-action.model';
import { toUnixTimestamp, fromUnixTimestamp } from '../../../../../../shared/utils/date-utils';

interface RemediationAction {
  id: number;
  remediationAction: string;
  dueDate: string;
  status: string;
  completionDate: string;
  comments: string;
  currentRemediationAction: string;
  currentDueDate: string;
  currentStatus: string;
  currentCompletionDate: string;
  currentComments: string;
  isSaved?: boolean; // Track if action is saved in backend
}

@Component({
  selector: 'app-ddq-specific-fields',
  standalone: true,
  imports: [
    CommonModule,
    FormFieldComponent,
    AzButtonComponent,
    AzDatePickerComponent,
    AzSelectorComponent,
    StatusChangeModalComponent,
    AzDocumentUpdateModalComponent,
  ],
  templateUrl: './ddq-specific-fields.component.html',
  styleUrl: './ddq-specific-fields.component.scss',
})
export class DdqSpecificFieldsComponent implements OnInit, OnChanges {
  @Input() legalEntityId: number | null = null;
  @Input() ddqNominee = '';
  @Input() contactEmail = '';
  @ViewChild(AzDocumentUpdateModalComponent) uploadModal?: AzDocumentUpdateModalComponent;

  isEditing = false;
  isSaving = false;
  isEditingRemediationActions = false;
  isSavingRemediationActions = false;
  showConfirmModal = false;
  showProcessingModal = false;
  processingModalTitle = 'Processing';
  processingResultTitle = 'Loading...';
  showWarningModal = false;
  showSuccessModal = false;
  showUploadSuccessModal = false;
  showErrorModal = false;
  showRemediationCompletionConfirmModal = false;
  isUploadModalOpen = false;
  isUploading = false;
  errorModalTitle = 'Warning';
  errorMessage = '';
  documentTypes: string[] = ['DDQ (Completed)']; // Document types for DDQ
  allowedFileTypes: string[] = ['docx']; // Allowed file types

  private originalDdqNominee = '';
  private originalContactEmail = '';
  private ddqFieldsCreatedDate: number = 0;
  private ddqFieldsLastUpdatedDate: number | null = null;
  private pendingDdqFieldsId: number | null = null; // Store ID from POST response

  currentDdqNominee = '';
  currentContactEmail = '';

  remediationActions: RemediationAction[] = [];
  statusOptions = ['Not started', 'In Progress', 'Completed', 'Cancelled'];

  constructor(
    public userPermissions: UserPermissionsService,
    private logger: LoggerService,
    private legalEntityService: LegalEntityService,
    private selectedLegalEntity: SelectedLegalEntityService,
    @Optional() private legalEntityDetailComponent: LegalEntityDetailComponent,
  ) {}

  ngOnInit(): void {
    this.currentDdqNominee = this.ddqNominee;
    this.currentContactEmail = this.contactEmail;
    this.originalDdqNominee = this.ddqNominee;
    this.originalContactEmail = this.contactEmail;

    this.loadDdqSpecificFields();
    this.loadRemediationActions().subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isEditing) {
      if (changes['ddqNominee']) {
        this.currentDdqNominee = this.ddqNominee;
        this.originalDdqNominee = this.ddqNominee;
      }
      if (changes['contactEmail']) {
        this.currentContactEmail = this.contactEmail;
        this.originalContactEmail = this.contactEmail;
      }
    }

    if (changes['legalEntityId'] && !changes['legalEntityId'].firstChange) {
      this.resetState();
    }
  }

  private loadDdqSpecificFields(): void {
    if (!this.legalEntityId) {
      this.logger.warn('No legal entity ID provided for loading DDQ-specific fields');
      return;
    }

    this.selectedLegalEntity.setDdqFieldsLoading(true);

    this.legalEntityService.getDdqSpecificFields(this.legalEntityId).subscribe({
      next: (response) => {
        if (response) {
          this.ddqFieldsCreatedDate = response.created_date;
          this.ddqFieldsLastUpdatedDate = response.last_updated_date;
          this.currentDdqNominee = response.ddq_nominee || '';
          this.currentContactEmail = response.contact_email || '';
          this.originalDdqNominee = response.ddq_nominee || '';
          this.originalContactEmail = response.contact_email || '';
        }
        this.selectedLegalEntity.setDdqFieldsLoading(false);
      },
      error: (error) => {
        this.logger.error('Error loading DDQ-specific fields', error);
        this.selectedLegalEntity.setDdqFieldsLoading(false);
        // Keep the default values if there's an error (DDQ fields don't exist yet)
      },
    });
  }

  private loadRemediationActions(): Observable<void> {
    if (!this.legalEntityId) {
      this.logger.warn('No legal entity ID provided for loading remediation actions');
      return of(undefined);
    }

    this.selectedLegalEntity.setRemediationActionsLoading(true);

    return this.legalEntityService.getRemediationActions(this.legalEntityId).pipe(
      map((dtos) => {
        if (dtos && dtos.length > 0) {
          this.remediationActions = dtos.map((dto) => this.mapFromDto(dto));
        } else {
          // If no actions exist, initialize with one empty template
          this.initializeRemediationActions();
        }
        this.selectedLegalEntity.setRemediationActionsLoading(false);
      }),
      catchError(() => {
        this.logger.error('Error loading remediation actions', {
          legalEntityId: this.legalEntityId,
        });
        // On error, keep the default initialized template
        this.initializeRemediationActions();
        this.selectedLegalEntity.setRemediationActionsLoading(false);
        return of(undefined);
      }),
    );
  }

  private mapFromDto(dto: RemediationActionDto): RemediationAction {
    const dueDate = fromUnixTimestamp(dto.due_date);
    const completionDate = fromUnixTimestamp(dto.date_of_completion);

    return {
      id: dto.id ?? 0,
      remediationAction: dto.remediation_action || '',
      dueDate: dueDate,
      status: dto.status || 'Not started',
      completionDate: completionDate,
      comments: dto.comments || '',
      currentRemediationAction: dto.remediation_action || '',
      currentDueDate: dueDate,
      currentStatus: dto.status || 'Not started',
      currentCompletionDate: completionDate,
      currentComments: dto.comments || '',
      isSaved: true, // Actions from backend are saved
    };
  }

  private mapToDto(action: RemediationAction): RemediationActionDto {
    if (!this.legalEntityId) {
      throw new Error('Legal entity ID is required to save remediation action');
    }

    return {
      id: action.id > 0 ? action.id : null, // null for new actions
      remediation_action: action.currentRemediationAction,
      due_date: toUnixTimestamp(action.currentDueDate),
      status: action.currentStatus,
      date_of_completion: toUnixTimestamp(action.currentCompletionDate),
      comments: action.currentComments,
      legal_entity_id: this.legalEntityId,
      created_date: 0, // Backend will set this
      last_updated_date: null, // Backend will set this
    };
  }

  private validateRemediationAction(action: RemediationAction): boolean {
    // Validation: remediation action text is required if action exists
    return action.currentRemediationAction.trim() !== '';
  }

  private saveRemediationActions(): Observable<RemediationAction[]> {
    // Filter actions that have changes or are new
    const actionsToSave = this.remediationActions.filter(
      (action) =>
        this.validateRemediationAction(action) &&
        (action.currentRemediationAction !== action.remediationAction ||
          action.currentDueDate !== action.dueDate ||
          action.currentStatus !== action.status ||
          action.currentCompletionDate !== action.completionDate ||
          action.currentComments !== action.comments),
    );

    if (actionsToSave.length === 0) {
      this.logger.info('No remediation actions to save');
      return of([]);
    }

    this.logger.info(`Saving ${actionsToSave.length} remediation actions`, {
      legalEntityId: this.legalEntityId,
    });

    // Create an array of save observables (POST for new, PUT for existing)
    const saveObservables = actionsToSave.map((action) => {
      const dto = this.mapToDto(action);

      // If id is null or 0, it's a new action (POST)
      if (!dto.id || dto.id === 0) {
        return this.legalEntityService.createRemediationAction(dto).pipe(
          map((createdDto) => this.mapFromDto(createdDto)),
          catchError((error) => {
            this.logger.error('Failed to create remediation action', {
              action: dto.remediation_action,
              error,
            });

            // Show error modal with backend message or timeout message
            const errorInfo = this.getErrorMessage(
              error,
              'An error occurred while creating the remediation action.',
            );
            this.errorMessage = errorInfo.message;
            this.errorModalTitle = 'Warning';
            this.showErrorModal = true;
            this.isSavingRemediationActions = false;
            this.showProcessingModal = false;

            return of(null); // Return null on error to continue with other saves
          }),
        );
      } else {
        // Existing action (PUT)
        return this.legalEntityService.updateRemediationAction(dto).pipe(
          map((updatedDto) => this.mapFromDto(updatedDto)),
          catchError((error) => {
            this.logger.error('Failed to update remediation action', {
              id: dto.id,
              action: dto.remediation_action,
              error,
            });

            // Show error modal with backend message or timeout message
            const errorInfo = this.getErrorMessage(
              error,
              'An error occurred while updating the remediation action.',
            );
            this.errorMessage = errorInfo.message;
            this.errorModalTitle = 'Warning';
            this.showErrorModal = true;
            this.isSavingRemediationActions = false;
            this.showProcessingModal = false;

            return of(null); // Return null on error to continue with other saves
          }),
        );
      }
    });

    // Execute all saves in parallel
    return forkJoin(saveObservables).pipe(
      map((results) => {
        // Filter out null results (failed saves)
        const successfulSaves = results.filter((result) => result !== null) as RemediationAction[];

        this.logger.info(
          `Successfully saved ${successfulSaves.length} of ${actionsToSave.length} remediation actions`,
        );

        // Update isSaved flag on successful saves
        successfulSaves.forEach((savedAction) => {
          const localAction = this.remediationActions.find((a) => a.id === savedAction.id);
          if (localAction) {
            localAction.isSaved = true;
            // For new actions that got a backend ID, update the local ID
            if (savedAction.id && savedAction.id > 0 && localAction.id === 0) {
              localAction.id = savedAction.id;
            }
          }
        });

        // Check if any saves failed
        if (successfulSaves.length < actionsToSave.length) {
          const failedCount = actionsToSave.length - successfulSaves.length;
          this.logger.warn(`${failedCount} remediation action(s) failed to save`);
          // Could show a user-friendly error message here
        }

        return successfulSaves;
      }),
    );
  }

  private initializeRemediationActions(): void {
    this.remediationActions = [
      {
        id: 0, // ID 0 indicates unsaved action
        remediationAction: '',
        dueDate: '',
        status: 'Not started',
        completionDate: '',
        comments: '',
        currentRemediationAction: '',
        currentDueDate: '',
        currentStatus: 'Not started',
        currentCompletionDate: '',
        currentComments: '',
        isSaved: false, // Initial template is not saved
      },
    ];
  }

  private resetState(): void {
    this.isEditing = false;
    this.isSaving = false;
    this.isEditingRemediationActions = false;
    this.isSavingRemediationActions = false;
    this.currentDdqNominee = this.ddqNominee;
    this.currentContactEmail = this.contactEmail;
    this.originalDdqNominee = this.ddqNominee;
    this.originalContactEmail = this.contactEmail;
    this.loadDdqSpecificFields();
    this.loadRemediationActions().subscribe();
  }

  get hasChanges(): boolean {
    const basicFieldsChanged =
      this.currentDdqNominee !== this.originalDdqNominee ||
      this.currentContactEmail !== this.originalContactEmail;

    return basicFieldsChanged;
  }

  get hasRemediationChanges(): boolean {
    return this.remediationActions.some(
      (action) =>
        action.currentRemediationAction !== action.remediationAction ||
        action.currentDueDate !== action.dueDate ||
        action.currentStatus !== action.status ||
        action.currentCompletionDate !== action.completionDate ||
        action.currentComments !== action.comments,
    );
  }

  get hasEmptyRemediationActions(): boolean {
    return this.remediationActions.some((action) => action.currentRemediationAction.trim() === '');
  }

  private isValidEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emailPattern.test(email.trim());
  }

  get hasInvalidEmail(): boolean {
    const email = this.isEditing ? this.currentContactEmail : this.contactEmail;
    return email.trim() !== '' && !this.isValidEmail(email);
  }

  get hasEmptyEmail(): boolean {
    const email = this.isEditing ? this.currentContactEmail : this.contactEmail;
    return this.isEditing && email.trim() === '';
  }

  get hasEmptyDdqNominee(): boolean {
    const nominee = this.isEditing ? this.currentDdqNominee : this.ddqNominee;
    return this.isEditing && nominee.trim() === '';
  }

  get canSave(): boolean {
    // Both DDQ Nominee and Email are required, and email must be valid
    return (
      this.currentDdqNominee.trim() !== '' &&
      this.currentContactEmail.trim() !== '' &&
      this.isValidEmail(this.currentContactEmail)
    );
  }

  get canEdit(): boolean {
    return this.userPermissions.hasEditEntityIdentificationPermission();
  }

  get canSeeDdqFile(): boolean {
    // All BCR roles can see the DDQ File section (title and documents)
    return this.userPermissions.hasAnyBcrRole();
  }

  /**
   * Check if user has role and status permissions to edit remediation actions
   * (regardless of edit mode state)
   */
  private hasRemediationActionPermissions(): boolean {
    const statusId = this.selectedLegalEntity.bcrStatusId();
    const hasPstRole = this.userPermissions.hasPstMemberRole();
    const hasPoRole = this.userPermissions.hasPrivacyOfficeRole();
    const hasDdqNomineeRole = this.userPermissions.hasDdqNomineeRole();

    // Status 11 (Pending: DDQ Under Review): Only PST Members
    if (statusId === 11) {
      return hasPstRole;
    }

    // Status 12 (Pending: Pending PO Approval): Only PO Members
    if (statusId === 12) {
      return hasPoRole;
    }

    // Status 14 (Pending: Pending Remediation Actions) or Status 15 (Incomplete: Assessed but Not Ready to Join BCR):
    // PST Members, PO Members, and DDQ Nominees
    if (statusId === 14 || statusId === 15) {
      return hasPstRole || hasPoRole || hasDdqNomineeRole;
    }

    // All other statuses: read-only
    return false;
  }

  get canEditRemediationActions(): boolean {
    // Only editable when in remediation actions edit mode AND user has permissions
    return this.isEditingRemediationActions && this.hasRemediationActionPermissions();
  }

  get canEditRemediationActionsButton(): boolean {
    // Check if user has permission to edit based on role and status (without edit mode check)
    return this.hasRemediationActionPermissions();
  }

  get hasAtLeastOneSavedAction(): boolean {
    // Button is enabled only if there's at least one saved action
    return this.remediationActions.some((action) => action.isSaved === true);
  }

  getActionNumber(index: number): number {
    // Calculate the action number based only on saved actions
    // Returns descending number (newest saved action gets highest number)
    const savedActionsCount = this.remediationActions.filter((action) => action.isSaved).length;
    const savedActionsBeforeThisIndex = this.remediationActions
      .slice(0, index + 1)
      .filter((action) => action.isSaved).length;
    return savedActionsCount - savedActionsBeforeThisIndex + 1;
  }

  get shouldShowApprovalButtons(): boolean {
    // Show Approve/Reject buttons based on status and role:
    // - Status 11 (DDQ Under Review): Only PST Member can approve/reject
    // - Status 12 (Pending PO Approval): Only Privacy Office can approve/reject
    const statusId = this.selectedLegalEntity.bcrStatusId();
    const hasPstRole = this.userPermissions.hasPstMemberRole();
    const hasPoRole = this.userPermissions.hasPrivacyOfficeRole();

    if (statusId === 11) {
      return hasPstRole;
    } else if (statusId === 12) {
      return hasPoRole;
    }

    return false;
  }

  get shouldShowUploadButton(): boolean {
    // Show upload button when:
    // - User is PST Member or DDQ Nominee
    const statusId = this.selectedLegalEntity.bcrStatusId();
    const canUpload =
      this.userPermissions.hasPstMemberRole() || this.userPermissions.hasDdqNomineeRole();

    // Show if user can upload AND (no docs exist OR status is Pending DDQ)
    return canUpload && statusId === 10;
  }

  get ddqFieldsId(): number | null {
    // Get the DDQ fields ID from entity detail for approve/reject actions
    const entityDetail = this.selectedLegalEntity.entityDetail();
    return entityDetail?.ddq_specific_fields?.id ?? null;
  }

  get ddqDocuments(): Array<{
    document_file_id: number;
    name: string;
    version: number;
    document_type: string;
    upload_status: string;
    uploaded_by: string;
    comments: string | null;
    created_date: number;
    s3_uri: string | null;
    size: number | null;
  }> {
    // Get DDQ documents from entity detail signal
    const entityDetail = this.selectedLegalEntity.entityDetail();
    return entityDetail?.ddq_documents || [];
  }

  get ddqDocumentFileId(): number | null {
    // Get the document_file_id from the first DDQ document (most recent)
    return this.ddqDocuments.length > 0 ? this.ddqDocuments[0].document_file_id : null;
  }

  toggleEdit(): void {
    if (!this.isEditing) {
      this.originalDdqNominee = this.currentDdqNominee;
      this.originalContactEmail = this.currentContactEmail;
    }
    this.isEditing = !this.isEditing;
  }

  toggleRemediationEdit(): void {
    if (!this.isEditingRemediationActions) {
      // Store original state when entering edit mode
      this.remediationActions.forEach((action) => {
        action.remediationAction = action.currentRemediationAction;
        action.dueDate = action.currentDueDate;
        action.status = action.currentStatus;
        action.completionDate = action.currentCompletionDate;
        action.comments = action.currentComments;
      });
    }
    this.isEditingRemediationActions = !this.isEditingRemediationActions;
  }

  discard(): void {
    // Revert DDQ fields to original values
    this.currentDdqNominee = this.originalDdqNominee;
    this.currentContactEmail = this.originalContactEmail;

    this.isEditing = false;
  }

  discardRemediationActions(): void {
    // Check if there are any saved actions
    const hasSavedActions = this.remediationActions.some((action) => action.isSaved);

    if (hasSavedActions) {
      // If there are saved actions, remove only unsaved ones
      this.remediationActions = this.remediationActions.filter((action) => action.isSaved);

      // Restore current values to original values for saved actions
      this.remediationActions.forEach((action) => {
        action.currentRemediationAction = action.remediationAction;
        action.currentDueDate = action.dueDate;
        action.currentStatus = action.status;
        action.currentCompletionDate = action.completionDate;
        action.currentComments = action.comments;
      });
    } else {
      // If there are NO saved actions, just restore current values (don't remove the blank form)
      this.remediationActions.forEach((action) => {
        action.currentRemediationAction = action.remediationAction;
        action.currentDueDate = action.dueDate;
        action.currentStatus = action.status;
        action.currentCompletionDate = action.completionDate;
        action.currentComments = action.comments;
      });
    }

    this.isEditingRemediationActions = false;
  }

  saveChanges(): void {
    if (!this.legalEntityId || !this.hasChanges) {
      return;
    }

    // Perform the save operation
    this.performSave();
  }

  saveRemediationChanges(): void {
    if (!this.legalEntityId || !this.hasRemediationChanges) {
      return;
    }

    // Proceed with save directly
    this.performRemediationSave();
  }

  performRemediationSave(): void {
    this.isSavingRemediationActions = true;
    this.processingModalTitle = 'Saving Remediation Actions';
    this.processingResultTitle = 'Saving your changes...';
    this.showProcessingModal = true;

    this.saveRemediationActions().subscribe({
      next: () => {
        // Reload remediation actions from backend to get the actual saved state
        // This removes any unsaved empty actions that were filtered out during save
        this.loadRemediationActions().subscribe(() => {
          this.isSavingRemediationActions = false;
          this.isEditingRemediationActions = false;
          this.showProcessingModal = false;
          this.logger.info('Remediation actions saved successfully');

          // Reload entity detail to update toolbar status
          this.selectedLegalEntity.reloadEntityDetail();

          // After successful save and reload, check if all remediation actions are Completed or Cancelled
          const savedActions = this.remediationActions.filter((action) => action.isSaved);
          const allCompleted =
            savedActions.length > 0 &&
            savedActions.every(
              (action) => action.status === 'Completed' || action.status === 'Cancelled',
            );

          // If all actions are completed/cancelled, show notification modal
          if (allCompleted) {
            setTimeout(() => {
              this.showRemediationCompletionConfirmModal = true;
            }, 100);
          }
        });
      },
      error: (error) => {
        this.logger.error('Error saving remediation actions', error);
        this.isSavingRemediationActions = false;
        this.showProcessingModal = false;

        const backendMessage =
          error.error?.message ||
          error.message ||
          'An error occurred while saving remediation actions.';
        this.errorMessage = backendMessage;
        this.errorModalTitle = 'Warning';
        this.showErrorModal = true;
      },
    });
  }

  onConfirmSave(): void {
    this.showConfirmModal = false;

    // Use pendingDdqFieldsId if available (from POST response), otherwise use getter
    const ddqFieldsId = this.pendingDdqFieldsId ?? this.ddqFieldsId;

    // Call the notify endpoint before showing success modal
    if (!ddqFieldsId || !this.legalEntityId) {
      this.logger.error('Cannot notify: DDQ fields ID or legal entity ID is missing');
      return;
    }

    // Show processing modal while notifying
    this.processingModalTitle = 'Notifying DDQ Nominee';
    this.processingResultTitle = 'Sending notification...';
    this.showProcessingModal = true;
    this.legalEntityService
      .notifyDdqNominee({
        id: ddqFieldsId,
        ddq_nominee: this.currentDdqNominee,
        contact_email: this.currentContactEmail,
        legal_entity_id: this.legalEntityId,
        created_date: this.ddqFieldsCreatedDate,
        last_updated_date: this.ddqFieldsLastUpdatedDate || Date.now(),
      })
      .subscribe({
        next: () => {
          this.showProcessingModal = false;

          // Clear the pending ID after successful notification
          this.pendingDdqFieldsId = null;

          // Reload entity detail AFTER notification completes
          this.selectedLegalEntity.reloadEntityDetail();

          // Reload entity detail using parent component's method
          if (this.legalEntityDetailComponent) {
            this.legalEntityDetailComponent.loadEntityStatus();
          }

          // Notify other components about the update
          this.selectedLegalEntity.notifyStatusChangeApproved();

          setTimeout(() => {
            this.showSuccessModal = true;
          }, 100);
        },
        error: (error) => {
          this.logger.error('Error notifying DDQ nominee', error);
          this.showProcessingModal = false;
          // Clear pending ID on error too
          this.pendingDdqFieldsId = null;
        },
      });
  }

  onCancelSave(): void {
    this.showConfirmModal = false;
    this.selectedLegalEntity.setActiveTabId('documents');
  }

  closeConfirmModal(): void {
    this.showConfirmModal = false;
  }

  closeSuccessModal(): void {
    this.showSuccessModal = false;
  }

  closeUploadSuccessModal(): void {
    this.showUploadSuccessModal = false;
  }

  closeErrorModal(): void {
    this.showErrorModal = false;
  }

  closeProcessingModal(): void {
    this.showProcessingModal = false;
  }

  closeRemediationCompletionConfirmModal(): void {
    this.showRemediationCompletionConfirmModal = false;
  }

  private performSave(): void {
    if (!this.legalEntityId) {
      return;
    }

    this.isSaving = true;
    this.processingModalTitle = 'Saving Changes';
    this.processingResultTitle = 'Saving your changes...';
    this.showProcessingModal = true;

    // Only save DDQ fields (remediation actions have their own save flow)
    this.saveDdqFields();
  }

  private saveDdqFields(): void {
    if (!this.legalEntityId) {
      return;
    }

    // Determine if we need to create or update based on whether DDQ fields exist
    if (this.ddqFieldsId === null) {
      // Create new DDQ-specific fields (POST)
      this.legalEntityService
        .createDdqSpecificFields({
          id: null,
          ddq_nominee: this.currentDdqNominee,
          contact_email: this.currentContactEmail,
          legal_entity_id: this.legalEntityId,
          created_date: 0,
          last_updated_date: null,
        })
        .subscribe({
          next: (response) => {
            // Store the ID from the response for immediate use in notification
            this.pendingDdqFieldsId = response.id;
            this.ddqFieldsCreatedDate = response.created_date;
            this.ddqFieldsLastUpdatedDate = response.last_updated_date;
            // DDQ fields created successfully
            this.handleSaveSuccess();
          },
          error: (error) => {
            this.handleSaveError(error);
          },
        });
    } else {
      // Update existing DDQ-specific fields (PUT)
      this.legalEntityService
        .updateDdqSpecificFields({
          id: this.ddqFieldsId,
          ddq_nominee: this.currentDdqNominee,
          contact_email: this.currentContactEmail,
          legal_entity_id: this.legalEntityId,
          created_date: this.ddqFieldsCreatedDate,
          last_updated_date: this.ddqFieldsLastUpdatedDate || Date.now(),
        })
        .subscribe({
          next: () => {
            this.handleSaveSuccess();
          },
          error: (error) => {
            this.handleSaveError(error);
          },
        });
    }
  }

  private handleSaveSuccess(): void {
    // Update the original values after successful save
    this.originalDdqNominee = this.currentDdqNominee;
    this.originalContactEmail = this.currentContactEmail;

    // Reload remediation actions from backend to get updated ids and timestamps
    this.loadRemediationActions().subscribe();

    this.isSaving = false;
    this.isEditing = false;

    // Don't reload here - will reload after notification is sent

    // Close processing modal and show confirmation modal after successful save
    this.showProcessingModal = false;
    setTimeout(() => {
      this.showConfirmModal = true;
    }, 100);
  }

  private handleSaveError(error: unknown): void {
    this.isSaving = false;
    this.showProcessingModal = false;
    this.logger.error('Error saving DDQ-Specific Fields', error);

    // Check if it's a timeout error and show appropriate message
    const errorInfo = this.getErrorMessage(
      error,
      'An error occurred while saving DDQ fields. Please try again.',
    );

    // Show error modal
    this.errorMessage = errorInfo.message;
    this.errorModalTitle = 'Warning';
    this.showErrorModal = true;
  }

  onDdqNomineeChange(value: string): void {
    this.currentDdqNominee = value;
  }

  onContactEmailChange(value: string): void {
    this.currentContactEmail = value;
  }

  handleUpload(): void {
    // Validate that Contact Email is not empty
    if (!this.currentContactEmail || this.currentContactEmail.trim() === '') {
      this.logger.warn('Cannot upload: Contact Email field is empty');
      this.showWarningModal = true;
      return;
    }

    this.logger.info('Opening DDQ file upload modal');
    this.isUploadModalOpen = true;
  }

  closeWarningModal(): void {
    this.showWarningModal = false;
  }

  closeUploadModal(): void {
    this.isUploadModalOpen = false;
    // Reset the form when modal is closed
    if (this.uploadModal) {
      this.uploadModal.resetForm();
    }
  }

  onFileUpload(event: { file: File | null; documentType: string; comments: string }): void {
    if (!event.file || !this.legalEntityId) {
      this.logger.error('No file selected or legal entity ID missing');
      return;
    }

    this.isUploading = true;
    this.logger.info('Starting DDQ file upload...', {
      fileName: event.file.name,
      documentType: event.documentType,
      fileSize: event.file.size,
    });

    // Step 1: Generate presigned URL with comments
    const uploadRequest = {
      document_type_name: event.documentType,
      comments: event.comments || undefined,
    };

    this.legalEntityService.generateDdqUploadUrl(this.legalEntityId, uploadRequest).subscribe({
      next: (uploadResponse) => {
        this.logger.info('Presigned POST generated', {
          documentFileId: uploadResponse.document_file_id,
          s3Key: uploadResponse.s3_key,
        });

        // Step 2: Upload file to S3 using presigned POST
        this.legalEntityService.uploadToS3(event.file!, uploadResponse.presigned_post).subscribe({
          next: (httpEvent) => {
            // Check if upload is complete (Response event)
            if (httpEvent.type === HttpEventType.Response) {
              this.logger.info('File uploaded to S3 successfully');

              // Step 3: Confirm upload (comments already sent in step 1)
              const confirmRequest = {
                document_file_id: uploadResponse.document_file_id,
              };

              this.legalEntityService
                .confirmDdqUpload(this.legalEntityId!, confirmRequest)
                .subscribe({
                  next: (confirmResponse) => {
                    this.isUploading = false;
                    this.isUploadModalOpen = false;

                    this.logger.info('DDQ upload confirmed successfully', {
                      newStatus: confirmResponse.new_bcr_status,
                      notificationSent: confirmResponse.notification_sent,
                    });

                    // Reset the upload modal form after successful upload
                    if (this.uploadModal) {
                      this.uploadModal.resetForm();
                    }

                    // Reload entity detail to update status
                    // This will automatically trigger loadDdqDocuments via reloadEntityDetail$ subscription
                    this.selectedLegalEntity.reloadEntityDetail();

                    // Show success modal for upload
                    this.showUploadSuccessModal = true;
                    this.showWarningModal = false;
                  },
                  error: (error) => {
                    this.isUploading = false;
                    this.isUploadModalOpen = false;
                    this.logger.error('Failed to confirm DDQ upload', error);

                    // Reset the upload modal form on error
                    if (this.uploadModal) {
                      this.uploadModal.resetForm();
                    }

                    // Check if it's a timeout error and show appropriate message
                    const errorInfo = this.getErrorMessage(
                      error,
                      'Failed to confirm upload. Please try again.',
                    );

                    // Show error modal
                    this.errorMessage = errorInfo.message;
                    this.errorModalTitle = 'Warning';
                    this.showErrorModal = true;
                  },
                });
            }
          },
          error: (error) => {
            this.isUploading = false;
            this.isUploadModalOpen = false;
            this.logger.error('Failed to upload file to S3', error);

            // Reset the upload modal form on error
            if (this.uploadModal) {
              this.uploadModal.resetForm();
            }

            // Check if it's a timeout error and show appropriate message
            const errorInfo = this.getErrorMessage(
              error,
              'Failed to upload file to storage. Please try again.',
            );

            // Show error modal
            this.errorMessage = errorInfo.message;
            this.errorModalTitle = 'Warning';
            this.showErrorModal = true;
          },
        });
      },
      error: (error) => {
        this.isUploading = false;
        this.isUploadModalOpen = false;
        this.logger.error('Failed to generate presigned URL', error);

        // Reset the upload modal form on error
        if (this.uploadModal) {
          this.uploadModal.resetForm();
        }

        // Check if it's a timeout error and show appropriate message
        const errorInfo = this.getErrorMessage(
          error,
          'Failed to initiate file upload. Please try again.',
        );

        // Show error modal
        this.errorMessage = errorInfo.message;
        this.errorModalTitle = 'Warning';
        this.showErrorModal = true;
      },
    });
  }

  onRemediationActionChange(action: RemediationAction, value: string): void {
    action.currentRemediationAction = value;
  }

  onDueDateChange(action: RemediationAction, value: string): void {
    action.currentDueDate = value;
  }

  onStatusChange(action: RemediationAction, value: string): void {
    const previousStatus = action.currentStatus;
    action.currentStatus = value;

    // Auto-populate completion date when status changes to Completed or Cancelled
    if ((value === 'Completed' || value === 'Cancelled') && previousStatus !== value) {
      if (!action.currentCompletionDate) {
        // Set to current date in ISO format (YYYY-MM-DD)
        const today = new Date();
        const isoDate = today.toISOString().split('T')[0];
        action.currentCompletionDate = isoDate;
      }
    }

    // Clear completion date if status changes away from Completed or Cancelled
    if (value !== 'Completed' && value !== 'Cancelled') {
      action.currentCompletionDate = '';
    }
  }

  onCompletionDateChange(action: RemediationAction, value: string): void {
    action.currentCompletionDate = value;
  }

  onCommentsChange(action: RemediationAction, value: string): void {
    action.currentComments = value;
  }

  addRemediationAction(): void {
    this.remediationActions.push({
      id: 0, // ID 0 indicates new unsaved action
      remediationAction: '',
      dueDate: '',
      status: 'Not started',
      completionDate: '',
      comments: '',
      currentRemediationAction: '',
      currentDueDate: '',
      currentStatus: 'Not started',
      currentCompletionDate: '',
      currentComments: '',
      isSaved: false, // New action is not saved yet
    });
    this.logger.info('Added new remediation action');
  }

  onApproveDdq(): void {
    if (!this.legalEntityId) {
      this.logger.error('Cannot approve DDQ: missing entity ID');
      return;
    }

    const statusId = this.selectedLegalEntity.bcrStatusId();

    // Status 12 (Pending PO Approval) - Privacy Office action
    if (statusId === 12) {
      this.logger.info('Approving DDQ (Privacy Office)', { entityId: this.legalEntityId });
      this.processingModalTitle = 'Approving DDQ';
      this.processingResultTitle = 'Submitting approval...';
      this.showProcessingModal = true;

      this.legalEntityService.ddqActionPrivacyOffice(this.legalEntityId, 'approve').subscribe({
        next: () => {
          // Reload entity detail to update status to "Pending LoA"
          this.selectedLegalEntity.reloadEntityDetail();
          this.showProcessingModal = false;
        },
        error: (error) => {
          const backendMessage =
            error.error?.message || error.message || 'An error occurred while approving the DDQ.';
          this.errorMessage = backendMessage;
          this.errorModalTitle = 'Warning';
          this.showErrorModal = true;
          this.showProcessingModal = false;
        },
      });
      return;
    }

    // Status 11 (DDQ Under Review) - PST Member action
    if (!this.ddqDocumentFileId) {
      this.logger.error('Cannot approve DDQ: missing document file ID');
      return;
    }

    this.logger.info('Approving DDQ (PST Member)', {
      entityId: this.legalEntityId,
      documentFileId: this.ddqDocumentFileId,
    });

    this.processingModalTitle = 'Approving DDQ';
    this.processingResultTitle = 'Submitting approval...';
    this.showProcessingModal = true;

    this.legalEntityService
      .ddqAction(this.legalEntityId, this.ddqDocumentFileId, 'approve')
      .subscribe({
        next: () => {
          // Reload entity detail to update status
          this.selectedLegalEntity.reloadEntityDetail();
          this.showProcessingModal = false;
        },
        error: (error) => {
          // Extract error message from backend
          const backendMessage =
            error.error?.message || error.message || 'An error occurred while approving the DDQ.';

          // Show error modal
          this.errorMessage = backendMessage;
          this.errorModalTitle = 'Warning';
          this.showErrorModal = true;
          this.showProcessingModal = false;
        },
      });
  }

  onRejectDdq(): void {
    if (!this.legalEntityId) {
      this.logger.error('Cannot reject DDQ: missing entity ID');
      return;
    }

    const statusId = this.selectedLegalEntity.bcrStatusId();

    // Status 12 (Pending PO Approval) - Privacy Office action
    if (statusId === 12) {
      this.logger.info('Rejecting DDQ (Privacy Office)', { entityId: this.legalEntityId });
      this.processingModalTitle = 'Rejecting DDQ';
      this.processingResultTitle = 'Submitting rejection...';
      this.showProcessingModal = true;

      this.legalEntityService.ddqActionPrivacyOffice(this.legalEntityId, 'reject').subscribe({
        next: (response) => {
          this.logger.info('DDQ rejected successfully (Privacy Office)', {
            newStatus: response.new_bcr_status,
            notificationSent: response.notification_sent,
          });
          // Reload entity detail to update status back to "Pending DDQ" (status 7)
          this.selectedLegalEntity.reloadEntityDetail();
          this.showProcessingModal = false;
        },
        error: (error) => {
          const backendMessage =
            error.error?.message || error.message || 'An error occurred while rejecting the DDQ.';
          this.errorMessage = backendMessage;
          this.errorModalTitle = 'Warning';
          this.showErrorModal = true;
          this.showProcessingModal = false;
        },
      });
      return;
    }

    // Status 11 (DDQ Under Review) - PST Member action
    if (!this.ddqDocumentFileId) {
      this.logger.error('Cannot reject DDQ: missing document file ID');
      return;
    }

    this.showProcessingModal = true;

    this.legalEntityService
      .ddqAction(this.legalEntityId, this.ddqDocumentFileId, 'reject')
      .subscribe({
        next: (response) => {
          this.logger.info('DDQ rejected successfully (PST Member)', {
            newStatus: response.new_bcr_status,
            notificationSent: response.notification_sent,
          });

          // Reload entity detail to update status
          this.selectedLegalEntity.reloadEntityDetail();
          this.showProcessingModal = false;
          // TODO: Show success/rejection modal
        },
        error: (error) => {
          // Extract error message from backend
          const backendMessage =
            error.error?.message || error.message || 'An error occurred while rejecting the DDQ.';

          // Show error modal
          this.errorMessage = backendMessage;
          this.errorModalTitle = 'Warning';
          this.showErrorModal = true;
          this.showProcessingModal = false;
        },
      });
  }

  downloadDdqDocument(document: (typeof this.ddqDocuments)[0]): void {
    if (!this.legalEntityId) {
      this.logger.error('Cannot download: No entity ID');
      return;
    }

    this.processingModalTitle = 'Download in Progress';
    this.processingResultTitle = 'Preparing your download...';
    this.showProcessingModal = true;

    this.legalEntityService
      .generateDownloadUrl(this.legalEntityId, document.document_file_id)
      .subscribe({
        next: (response) => {
          // Use the downloadFile helper method to trigger browser download
          this.legalEntityService.downloadFile(response.download_url, response.filename);
          this.showProcessingModal = false;
        },
        error: (error) => {
          // Extract error message from backend
          const backendMessage =
            error.error?.message ||
            error.message ||
            'An error occurred while downloading the DDQ document.';

          // Show error modal
          this.errorMessage = backendMessage;
          this.errorModalTitle = 'Warning';
          this.showErrorModal = true;
          this.showProcessingModal = false;
        },
      });
  }

  /**
   * Check if error is a timeout error
   */
  private isTimeoutError(error: unknown): boolean {
    const err = error as {
      name?: string;
      message?: string;
      status?: number;
      statusText?: string;
      error?: { message?: string };
    };
    return (
      err?.name === 'TimeoutError' ||
      (err?.message?.toLowerCase().includes('timeout') ?? false) ||
      err?.status === 504 || // Gateway Timeout
      err?.status === 408 || // Request Timeout
      (err?.error?.message?.toLowerCase().includes('timeout') ?? false) ||
      // Browser timeout: status 0 with Unknown Error
      (err?.status === 0 && err?.statusText === 'Unknown Error')
    );
  }

  /**
   * Get appropriate error message based on error type
   */
  private getErrorMessage(
    error: unknown,
    defaultMessage: string,
  ): { title: string; message: string } {
    if (this.isTimeoutError(error)) {
      return {
        title: 'Request Timeout',
        message:
          'The file took too long to process. Please check your connectivity and/or file size and try again.\n\nIf this error keeps occurring please contact support.',
      };
    }

    // Extract backend message or use default
    const err = error as { error?: { message?: string }; message?: string };
    const message = err.error?.message || err.message || defaultMessage;

    // Infer a sensible title from the default message (operation context)
    const op = defaultMessage.toLowerCase();
    let title = 'Request Failed';

    if (op.includes('upload')) {
      title = 'Upload Failed';
    } else if (op.includes('download')) {
      title = 'Download Failed';
    } else if (op.includes('save') || op.includes('update')) {
      title = 'Save Failed';
    }

    return {
      title,
      message,
    };
  }
}
